import { Space } from '@sravni/react-design-system';
import type { ReactNode } from 'react';
import React, { useCallback } from 'react';

import type { FormStepId } from 'shared/config/formStepId';
import { useAppDispatch, useAppSelector } from 'shared/lib';

import { isAnyStepOpenedInPopup, setFormIsOpenedInPopupByStepId } from 'entities/creditForm';
import { FormIcon, useFormStepDescriptionComponent, useFormStepTitleComponent } from 'entities/whiteLabels';

import { useUpdateScoreValue } from 'features/UpdateScoreValue';
import { useFormStepHeadersConfig } from 'features/useFormStepHeadersConfig';

import { CommonSummaryTexts } from 'widgets/AnketaWidget/ui/CommonSummary/CommonSummary.texts';

import { useStepper } from '../../lib';
import type { IOrderedStep, IStepperFormsSet } from '../../types';
import { WIDGET_BLOCKS } from '../AnketaWidget.config';

export interface ICommonSummaryProps {
  /**
   * Указывает, место в которое браузер проскролит пользователя
   * Если есть на странице - аккордион открыт
   * */
  scrollId?: string;
  onFormSubmit?: () => void;
  isExtendedData?: boolean;
  steps?: IStepperFormsSet;
  /**
   * Опция при которой поле будет дизейблится какое-то время и выводить нотификашку при попытке отредактировать
   * Имеет приоритет перед disabledFields
   */
  isWaitingToEdit?: boolean;
  shouldForceOpenPopup?: boolean;
  handleClickSubmitForm?: () => void;
  isPopupsClosable?: boolean;
  /**
   * Полный запрет на редактирование определенных или всех полей
   * Можно передать объект и отключить определенные поля, либо передать true и отключить все поля
   */
  disabledFields?: Partial<Record<FormStepId, boolean>> | true;
  onPopupClose?: (isSubmit?: boolean) => void;
  getCommonAlerts?: (formId: string, isFormForceOpened: boolean) => JSX.Element | null;
  isHideAccordionData?: boolean;
  accordionIconSize?: 36 | 48;
}

export const CommonSummary: FC<ICommonSummaryProps> = ({
  scrollId,
  onFormSubmit,
  isExtendedData,
  isWaitingToEdit = false,
  shouldForceOpenPopup = true,
  isPopupsClosable,
  onPopupClose,
  getCommonAlerts,
  disabledFields,
  steps,
  isHideAccordionData = false,
  accordionIconSize = 36,
}) => {
  const isAnyFormStepOpenedInPopup = useAppSelector(isAnyStepOpenedInPopup);
  const dispatch = useAppDispatch();
  useUpdateScoreValue();
  const TitleComponent = useFormStepTitleComponent();
  const DescriptionComponent = useFormStepDescriptionComponent();
  const { headersConfig: stepDefaultHeaders, isAlwaysShowDescription } = useFormStepHeadersConfig();
  const { stepsWithMultiStepsOrder, activeStep } = useStepper(steps);

  const onCloseByStep = useCallback(
    (stepId: FormStepId) => (isSubmit?: boolean) => {
      dispatch(setFormIsOpenedInPopupByStepId({ stepId, isOpened: false }));
      onPopupClose?.(isSubmit);
    },
    [dispatch, onPopupClose],
  );

  const setIsStepOpenInPopupById = useCallback(
    (formId: FormStepId) => () => {
      dispatch(setFormIsOpenedInPopupByStepId({ stepId: formId, isOpened: true }));
    },
    [dispatch],
  );

  const getStep = (step: IOrderedStep, index: number): ReactNode => {
    const Component = WIDGET_BLOCKS[step.formId as FormStepId].formStepComponent;
    const { title, description } = stepDefaultHeaders[step.formId as FormStepId];

    const isDisabled =
      (isWaitingToEdit || disabledFields === true || disabledFields?.[step.formId as FormStepId]) ?? false;

    const isFormForceOpened = activeStep?.formId === step.formId;

    const onCloseStepPopup = onCloseByStep(step.formId);
    const onOpenStepPopup = setIsStepOpenInPopupById(step.formId);

    return (
      <Component
        onFormSubmit={onFormSubmit}
        multipartFormId=""
        shouldHideDividers
        key={step.formId}
        isOpen={false}
        isSummary
        isFormForceOpened={isFormForceOpened}
        shouldForceOpenPopup={shouldForceOpenPopup}
        onPopupClose={onCloseStepPopup}
        onPopupOpen={onOpenStepPopup}
        isAnyFormStepOpenedInPopup={isAnyFormStepOpenedInPopup}
        isExtendedData={isExtendedData}
        isDisableEdit={isDisabled}
        isPopupClosable={isPopupsClosable === undefined ? !isFormForceOpened : isPopupsClosable}
        stepIndex={index}
        isHideAccordionData={isHideAccordionData}
        submitButtonText={CommonSummaryTexts.buttonTitle}
        icon={<FormIcon stepName={step.formId} />}
        accordionIconSize={accordionIconSize}
        TitleComponent={TitleComponent}
        DescriptionComponent={DescriptionComponent}
        defaultTitle={title}
        defaultDescription={description}
        isAlwaysShowDescription={isAlwaysShowDescription}
      >
        {getCommonAlerts?.(step.formId, isFormForceOpened)}
      </Component>
    );
  };

  return (
    <Space
      direction="vertical"
      size={accordionIconSize < 48 ? 12 : undefined}
      id={scrollId}
      className={isExtendedData ? 'h-pt-12' : ''}
    >
      {stepsWithMultiStepsOrder.map(getStep)}
    </Space>
  );
};
